import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bookmark } from 'lucide-react';
import SubtitleItem from './SubtitleItem';
import './Viewer.css';
import { API_BASE_URL, DATA_BASE_URL } from '../config.js';
import { isBookmarked, addBookmark, removeBookmark } from '../utils/bookmarkStorage';
// import { API_BASE_URL } from '../config.js';


// 상수 정의
const REPEAT_INTERVAL_MS = 1000; // 반복재생 간격 (1초)

const Viewer = () => {
  const { id, accessKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [subtitles, setSubtitles] = useState([]);
  const [currentSubtitle, setCurrentSubtitle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [showEnglish, setShowEnglish] = useState({}); // 각 자막의 영문 표시 상태
  const [showKorean, setShowKorean] = useState({}); // 각 자막의 한글 표시 상태
  const [isPlaying, setIsPlaying] = useState(false); // 오디오 재생 상태
  const [currentAudioPath, setCurrentAudioPath] = useState(null); // 현재 재생 중인 오디오 경로
  const [youtubePlayer, setYoutubePlayer] = useState(null); // YouTube Player 인스턴스
  const [youtubeRepeatActive, setYoutubeRepeatActive] = useState(false); // 유튜브 반복재생 상태
  const [currentYoutubeSegment, setCurrentYoutubeSegment] = useState(null); // 현재 반복 중인 구간
  const [playbackRate, setPlaybackRate] = useState(1); // 재생속도 (1 = 정상속도)
  const [bookmarkedSubtitles, setBookmarkedSubtitles] = useState({}); // 북마크 상태
  const audioRef = useRef(null);
  const repeatTimeoutRef = useRef(null); // 반복재생 타이머 참조
  const youtubeIntervalRef = useRef(null); // 유튜브 체크 인터벌 참조
  const isPlayingRef = useRef(false); // 최신 재생 상태 추적
  const currentAudioPathRef = useRef(null); // 최신 오디오 경로 추적

  // 관리자 모드 감지 (URL에 admin=true 파라미터가 있는지 확인)
  const isAdminMode = new URLSearchParams(location.search).get('admin') === 'true';

  // YouTube ID 추출 함수
  const extractYouTubeId = (url) => {
    // YouTube Shorts URL 패턴 추가
    const shortsRegExp = /^.*\/shorts\/([^#&?]*).*/;
    const shortsMatch = url.match(shortsRegExp);
    if (shortsMatch && shortsMatch[1].length === 11) {
      return shortsMatch[1];
    }

    // 기존 YouTube URL 패턴들
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const fetchContent = useCallback(async () => {
    try {
      // 토큰 가져오기
      const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

      // 토큰이 없으면 로그인 페이지로 리다이렉트
      // if (!token) {
      //   navigate('/login', { state: { from: location.pathname }, replace: true });
      //   return;
      // }

      const url = id === '0' ? `${API_BASE_URL}/free` : `${API_BASE_URL}/data/${id}/${accessKey}.json`;
      console.log(id)
      console.log(url)

      // 정적 JSON 파일에서 컨텐츠 데이터 로드
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          // 토큰이 유효하지 않으면 로그인 페이지로 리다이렉트
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login', { state: { from: location.pathname }, replace: true });
        } else if (response.status === 404) {
          setError('컨텐츠를 찾을 수 없거나 접근 권한이 없습니다.');
        } else {
          setError('컨텐츠를 불러올 수 없습니다.');
        }
        return;
      }

      const data = await response.json();

      // 새로운 JSON 형식 처리
      if (data.title && data.content) {
        // 새로운 형식: {title, url, content: [...]}
        setContent({
          id: id,
          title: data.title,
          youtubeUrl: data.url || `https://www.youtube.com/watch?v=${id}`, // url 필드 사용, 없으면 기본값
          youtubeId: data.url ? extractYouTubeId(data.url) : id // URL에서 ID 추출, 없으면 파라미터 사용
        });

        // content 배열을 subtitles 형식으로 변환
        const convertedSubtitles = data.content.map((item, index) => {
          // time 형식 "mm:ss-mm:ss"를 startTime, endTime으로 변환
          const parseTime = (timeStr) => {
            const [start, end] = timeStr.split('-');
            const parseTimeString = (str) => {
              const [mins, secs] = str.split(':').map(Number);
              return mins * 60 + secs;
            };
            return {
              startTime: parseTimeString(start),
              endTime: parseTimeString(end)
            };
          };

          const { startTime, endTime } = parseTime(item.time);

          return {
            english: item.eng,
            korean: item.kor,
            comment: item.comment || '',
            words: item.words || [],
            startTime: startTime,
            endTime: endTime,
            audioPath: item.audioPath // 서버에서 제공하는 오디오 경로 사용
          };
        });

        setSubtitles(convertedSubtitles);
      } else if (data.content && data.subtitles) {
        // 기존 형식: {content: {...}, subtitles: [...]}
        setContent(data.content);
        setSubtitles(data.subtitles);
      } else {
        setError('올바르지 않은 데이터 형식입니다.');
        return;
      }
    } catch (error) {
      setError('컨텐츠를 불러올 수 없습니다.');
      console.error('Content fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [id, accessKey, navigate, location.pathname]);

  useEffect(() => {
    console.log('Fetching content for:', id, accessKey);
    fetchContent();
  }, [id, accessKey, fetchContent]);

  // 북마크 상태 로드
  useEffect(() => {
    if (subtitles.length > 0 && id) {
      const bookmarkState = {};
      subtitles.forEach((_, index) => {
        bookmarkState[index] = isBookmarked(id, index);
      });
      setBookmarkedSubtitles(bookmarkState);
    }
  }, [subtitles, id]);

  const initYouTubePlayer = useCallback(() => {
    if (!content || !window.YT || !window.YT.Player) return;

    console.log('Initializing YouTube player with video ID:', content.youtubeId);

    new window.YT.Player('youtube-player', {
      height: '400',
      width: '100%',
      videoId: content.youtubeId,
      events: {
        'onReady': (event) => {
          console.log('YouTube player ready');
          setYoutubePlayer(event.target);
          // 초기 재생속도 설정
          if (playbackRate !== 1) {
            event.target.setPlaybackRate(playbackRate);
          }
        },
        'onError': (event) => {
          console.error('YouTube player error:', event.data);
        }
      }
    });
  }, [content, playbackRate]);

  // YouTube API 로드 및 초기화
  useEffect(() => {
    if (!window.YT) {
      // YouTube API 스크립트 로드
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      // API 준비 완료 콜백
      window.onYouTubeIframeAPIReady = () => {
        if (content) {
          initYouTubePlayer();
        }
      };
    } else if (content) {
      initYouTubePlayer();
    }
  }, [content, initYouTubePlayer]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    console.log('Unmounting component');
    return () => {
      if (repeatTimeoutRef.current) {
        clearTimeout(repeatTimeoutRef.current);
      }
      if (youtubeIntervalRef.current) {
        clearInterval(youtubeIntervalRef.current);
      }
    };
  }, []);



  const playAudio = async (audioPath) => {
    if (audioPath && audioRef.current) {
      try {
        // 기존 반복 타이머 정리
        if (repeatTimeoutRef.current) {
          clearTimeout(repeatTimeoutRef.current);
          repeatTimeoutRef.current = null;
        }

        // 토큰 가져오기
        const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

        // 토큰이 없으면 로그인 페이지로 리다이렉트
        // if (!token) {
        //   navigate('/login', { state: { from: location.pathname }, replace: true });
        //   return;
        // }

        // 오디오 소스 설정 (토큰을 쿼리 파라미터로 추가)
        audioRef.current.src = `${DATA_BASE_URL}${audioPath}?token=${encodeURIComponent(token)}`;
        // console.log('audioRef.current.src : ', audioRef.current.src)
        audioRef.current.loop = false; // 수동으로 반복 제어
        audioRef.current.playbackRate = playbackRate; // 재생속도 설정

        // 오디오 로드 대기
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Audio load timeout')), 5000);

          audioRef.current.onloadeddata = () => {
            clearTimeout(timeout);
            // 로드 완료 후 재생속도 재설정
            audioRef.current.playbackRate = playbackRate;
            resolve();
          };
          audioRef.current.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Audio load failed'));
          };
          audioRef.current.load();
        });

        // 재생 시작
        await audioRef.current.play();
        setIsPlaying(true);
        setCurrentAudioPath(audioPath);
        isPlayingRef.current = true;
        currentAudioPathRef.current = audioPath;
        console.log('Audio playing:', audioPath);
      } catch (error) {
        console.error('Audio play error:', error);
        alert('오디오 재생에 실패했습니다. 브라우저 설정을 확인해주세요.');
        setIsPlaying(false);
        setCurrentAudioPath(null);
        isPlayingRef.current = false;
        currentAudioPathRef.current = null;
      }
    } else {
      console.warn('No audio path available for this subtitle');
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // 반복 타이머 정리
    if (repeatTimeoutRef.current) {
      clearTimeout(repeatTimeoutRef.current);
      repeatTimeoutRef.current = null;
    }

    setIsPlaying(false);
    setCurrentAudioPath(null);
    isPlayingRef.current = false;
    currentAudioPathRef.current = null;
  };

  const scheduleRepeat = (audioPath) => {
    if (repeatTimeoutRef.current) {
      clearTimeout(repeatTimeoutRef.current);
    }

    // console.log('Scheduling repeat for:', audioPath, 'Current playing:', isPlayingRef.current);

    repeatTimeoutRef.current = setTimeout(() => {
      // ref를 사용하여 최신 상태 확인
      if (isPlayingRef.current && currentAudioPathRef.current === audioPath && audioRef.current) {
        console.log('Repeating audio after 1 second delay');
        audioRef.current.currentTime = 0;
        audioRef.current.playbackRate = playbackRate; // 반복재생 시에도 재생속도 유지
        audioRef.current.play().catch(error => {
          console.error('Repeat play error:', error);
          stopAudio();
        });
      } else {
        console.log('Repeat cancelled - playing:', isPlayingRef.current, 'path match:', currentAudioPathRef.current === audioPath);
      }
    }, REPEAT_INTERVAL_MS);
  };

  // 유튜브 반복재생 시작
  const startYoutubeRepeat = (player, startTime, endTime) => {
    // 기존 인터벌 정리
    if (youtubeIntervalRef.current) {
      clearInterval(youtubeIntervalRef.current);
    }

    console.log('Starting YouTube repeat:', startTime, 'to', endTime);

    // 새로운 인터벌 시작
    youtubeIntervalRef.current = setInterval(() => {
      if (player && player.getCurrentTime) {
        try {
          const currentTime = player.getCurrentTime();
          console.log('Checking time:', currentTime, 'End time:', endTime);

          // 구간 끝에 도달했으면 시작점으로 이동
          if (currentTime >= endTime - 0.3) {
            console.log('Looping back to:', startTime);
            player.seekTo(startTime, true);
          }
        } catch (error) {
          console.error('YouTube repeat error:', error);
        }
      }
    }, 300); // 300ms마다 체크
  };

  // 유튜브 반복재생 중지
  const stopYoutubeRepeat = () => {
    console.log('Stopping YouTube repeat');
    if (youtubeIntervalRef.current) {
      clearInterval(youtubeIntervalRef.current);
      youtubeIntervalRef.current = null;
    }
    setYoutubeRepeatActive(false);
    setCurrentYoutubeSegment(null);
    if (youtubePlayer && youtubePlayer.pauseVideo) {
      youtubePlayer.pauseVideo();
    }
  };

  // 시간 클릭 핸들러
  const handleTimeClick = (startTime, endTime, isRepeating) => {
    if (isRepeating) {
      stopYoutubeRepeat();
    } else {
      seekToTime(startTime, endTime);
    }
  };

  // 시간 클릭 시 유튜브 영상 이동 및 구간 반복재생
  const seekToTime = (startTime, endTime) => {
    console.log('seekToTime called:', startTime, endTime, 'Player:', !!youtubePlayer);

    if (youtubePlayer && youtubePlayer.seekTo) {
      // 새로운 구간 설정
      setCurrentYoutubeSegment({ startTime, endTime });
      setYoutubeRepeatActive(true);

      // 구간 시작으로 이동하고 재생
      youtubePlayer.seekTo(startTime, true);
      youtubePlayer.playVideo();

      // 반복재생 시작 (약간의 지연 후)
      setTimeout(() => {
        startYoutubeRepeat(youtubePlayer, startTime, endTime);
      }, 1000);
    } else {
      console.error('YouTube player not ready');
    }
  };

  const toggleAudio = (audioPath) => {
    if (isPlaying && currentAudioPath === audioPath) {
      stopAudio();
    } else {
      playAudio(audioPath);
    }
  };

  const handleSubtitleClick = (subtitle) => {
    setCurrentSubtitle(subtitle);

    // 첫 번째 클릭에서 오디오 활성화
    if (!audioEnabled) {
      setAudioEnabled(true);
    }

    if (subtitle.audioPath) {
      toggleAudio(subtitle.audioPath);
    } else {
      console.warn('No audio available for this subtitle');
    }
  };

  // 영문 표시/숨김 토글
  const toggleEnglish = (index, e) => {
    e.stopPropagation(); // 자막 클릭 이벤트 방지
    setShowEnglish(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // 한글 표시/숨김 토글
  const toggleKorean = (index, e) => {
    e.stopPropagation(); // 자막 클릭 이벤트 방지
    setShowKorean(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // 재생속도 변경 핸들러
  const handlePlaybackRateChange = (rate) => {
    setPlaybackRate(rate);

    // YouTube 플레이어 재생속도 변경
    if (youtubePlayer && youtubePlayer.setPlaybackRate) {
      youtubePlayer.setPlaybackRate(rate);
    }

    // 오디오 재생속도 변경
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  };

  // 전체 영문 토글
  const toggleAllEnglish = () => {
    const allVisible = subtitles.every((_, index) => showEnglish[index]);
    const newState = {};
    subtitles.forEach((_, index) => {
      newState[index] = !allVisible;
    });
    setShowEnglish(newState);
  };

  // 전체 한글 토글
  const toggleAllKorean = () => {
    const allVisible = subtitles.every((_, index) => showKorean[index]);
    const newState = {};
    subtitles.forEach((_, index) => {
      newState[index] = !allVisible;
    });
    setShowKorean(newState);
  };

  // 북마크 목록 보기
  const handleBookmarksClick = () => {
    // 로그인 상태 확인
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    const user = localStorage.getItem('user');

    if (!token || !user) {
      alert('북마크 목록을 보려면 로그인이 필요합니다.');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    navigate('/bookmarks');
  };

  // 북마크 토글
  const toggleBookmark = (index) => {
    // 로그인 상태 확인
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    const user = localStorage.getItem('user');

    if (!token || !user) {
      alert('북마크 기능을 사용하려면 로그인이 필요합니다.');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    const subtitle = subtitles[index];
    const currentlyBookmarked = bookmarkedSubtitles[index];

    if (currentlyBookmarked) {
      // 북마크 제거
      if (removeBookmark(id, index)) {
        setBookmarkedSubtitles(prev => ({
          ...prev,
          [index]: false
        }));
      }
    } else {
      // 북마크 추가
      if (addBookmark(id, content.title, index, subtitle, accessKey)) {
        setBookmarkedSubtitles(prev => ({
          ...prev,
          [index]: true
        }));
      }
    }
  };

  // 구간 시간 업데이트 (관리자 모드에서만 사용)
  const handleTimeUpdate = async (index, newStartTime, newEndTime) => {
    console.log('handleTimeUpdate called:', { index, newStartTime, newEndTime, isAdminMode });

    if (!isAdminMode) {
      console.log('Not in admin mode, skipping update');
      return;
    }

    try {
      // 로컬 상태 업데이트
      const updatedSubtitles = [...subtitles];
      updatedSubtitles[index] = {
        ...updatedSubtitles[index],
        startTime: newStartTime,
        endTime: newEndTime
      };
      setSubtitles(updatedSubtitles);
      console.log('Local state updated');

      // 서버에 업데이트 요청
      const token = localStorage.getItem('adminToken');
      if (!token) {
        console.log('No admin token found');
        alert('관리자 인증이 필요합니다.');
        return;
      }

      const url = `${API_BASE_URL}/api/admin/contents/${id}/subtitles/${index}/time`;
      const requestBody = {
        startTime: newStartTime,
        endTime: newEndTime
      };

      console.log('Making API request:', { url, requestBody, token: token.substring(0, 10) + '...' });

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.log('API error response:', errorData);
        throw new Error(errorData.error || '시간 업데이트에 실패했습니다.');
      }

      const responseData = await response.json();
      console.log('API success response:', responseData);
      console.log('Time updated successfully');
    } catch (error) {
      console.error('Time update error:', error);
      alert('시간 업데이트에 실패했습니다: ' + error.message);

      // 실패 시 원래 상태로 복원
      console.log('Restoring original state');
      fetchContent();
    }
  };

  if (loading) {
    return <div className="loading">로딩 중...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!content) {
    return <div className="error">컨텐츠를 찾을 수 없습니다.</div>;
  }

  return (
    <div className="viewer">
      <header className="viewer-header">
        <div className="header-content">
          <button
            className="back-button"
            onClick={() => navigate('/')}
            title="목록으로 돌아가기"
            aria-label="목록으로 돌아가기"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="back-button-text">목록으로</div>
        </div>
        <div className="header-right">
          <button
            className="bookmarks-button"
            onClick={handleBookmarksClick}
            title="나의 북마크"
            aria-label="나의 북마크"
          >
            <Bookmark size={20} />
            <span>북마크</span>
          </button>
          {isAdminMode && (
            <div className="admin-mode-indicator">
              <span className="admin-badge">관리자 모드</span>
              <span className="admin-help">구간 시간을 클릭하여 편집할 수 있습니다</span>
            </div>
          )}
        </div>
      </header>

      <div className="viewer-content">
        <div className="video-section">
          <div className="youtube-embed">
            <div id="youtube-player"></div>
          </div>
        </div>

        <div className="subtitles-section">
          <div className="subtitles-header">
            <div className="global-controls">
              <button
                className={`toggle-btn global ${subtitles.every((_, index) => showEnglish[index]) ? 'active' : ''}`}
                onClick={toggleAllEnglish}
                title="전체 영문 보이기/감추기"
              >
                EN
              </button>
              <button
                className={`toggle-btn global ${subtitles.every((_, index) => showKorean[index]) ? 'active' : ''}`}
                onClick={toggleAllKorean}
                title="전체 한글 보이기/감추기"
              >
                KO
              </button>
              <div className="playback-rate-control">
                <select
                  value={playbackRate}
                  onChange={(e) => handlePlaybackRateChange(parseFloat(e.target.value))}
                  className="playback-rate-select"
                  title="재생속도 설정"
                >
                  <option value={0.5}>0.5x</option>
                  <option value={0.75}>0.75x</option>
                  <option value={1}>1x</option>
                  <option value={1.25}>1.25x</option>
                  <option value={1.5}>1.5x</option>
                  <option value={2}>2x</option>
                </select>
              </div>
            </div>
          </div>
          <div className="subtitles-list">
            {subtitles.map((subtitle, index) => (
              <SubtitleItem
                key={index}
                subtitle={subtitle}
                index={index}
                isActive={currentSubtitle === subtitle}
                showEnglish={showEnglish[index]}
                showKorean={showKorean[index]}
                isPlaying={isPlaying}
                currentAudioPath={currentAudioPath}
                youtubeRepeatActive={youtubeRepeatActive}
                currentYoutubeSegment={currentYoutubeSegment}
                audioEnabled={audioEnabled}
                isAdminMode={isAdminMode}
                onTimeClick={handleTimeClick}
                onToggleEnglish={toggleEnglish}
                onToggleKorean={toggleKorean}
                onToggleAudio={toggleAudio}
                onSubtitleClick={handleSubtitleClick}
                onTimeUpdate={handleTimeUpdate}
                setAudioEnabled={setAudioEnabled}
                isBookmarked={bookmarkedSubtitles[index]}
                onToggleBookmark={toggleBookmark}
              />
            ))}
          </div>
        </div>
      </div>

      <audio
        ref={audioRef}
        controls
        style={{ display: 'none' }}
        preload="metadata"
        onError={(e) => {
          console.error('Audio error:', e);
          setIsPlaying(false);
          setCurrentAudioPath(null);
          if (repeatTimeoutRef.current) {
            clearTimeout(repeatTimeoutRef.current);
            repeatTimeoutRef.current = null;
          }
        }}
        onLoadStart={() => {
          console.log('Audio loading started');
          // 로드 시작 시 재생속도 설정
          if (audioRef.current) {
            audioRef.current.playbackRate = playbackRate;
          }
        }}
        onCanPlay={() => {
          console.log('Audio can play');
          // 재생 가능 시 재생속도 재설정
          if (audioRef.current) {
            audioRef.current.playbackRate = playbackRate;
          }
        }}
        onEnded={() => {
          console.log('Audio ended, current path:', currentAudioPath);
          if (currentAudioPath) {
            scheduleRepeat(currentAudioPath);
          }
        }}
        onPause={() => {
          console.log('Audio paused');
          // 수동으로 정지한 경우가 아니라면 상태를 유지
          if (audioRef.current && audioRef.current.currentTime === 0) {
            setIsPlaying(false);
          }
        }}
        onPlay={() => {
          console.log('Audio playing');
          setIsPlaying(true);
          // 재생 시작 시 재생속도 재설정
          if (audioRef.current) {
            audioRef.current.playbackRate = playbackRate;
          }
        }}
      />
    </div>
  );
};

export default Viewer;